import type { ExcelCellValue } from "@/excel/speckle/receiveTypes";
import {
  dereferenceSchema,
  getSchemaPrimitiveType,
  resolveAnyOfBranches,
  resolveNullableSchema,
  resolveObjectPropertySchema,
  resolveSchemaForValue,
  type SchemaNode,
} from "@/services/equation-excel-client-metadata/equationExcelWorkbookSchemaResolver";
import type { ExtendedDocument, ResolvedExtendedSchemaObject } from "@/services/open-api/openApi.types";
import { isRecord } from "@/utils/utils";

type FlattenedRow = {
  path: string;
  value: ExcelCellValue;
  type: string;
};

function valueType(value: unknown): string {
  if (value === null) {
    return "null";
  }

  if (Array.isArray(value) || typeof value === "object") {
    return "json";
  }

  return typeof value;
}

function runtimeSchemaType(value: unknown): "boolean" | "number" | "string" | "object" | "array" | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return "array";
  }

  const primitiveType = typeof value;

  switch (primitiveType) {
    case "boolean":
    case "number":
    case "string":
    case "object":
      return primitiveType;
    default:
      return undefined;
  }
}

function pickMatchingAnyOfBranch(
  apiSchema: ExtendedDocument,
  branches: SchemaNode[],
  actualValue: unknown
): SchemaNode | null {
  const runtimeType = runtimeSchemaType(actualValue);

  if (!runtimeType) {
    return null;
  }

  const candidates = branches.filter((branch) => {
    const branchType = getSchemaPrimitiveType(apiSchema, branch);

    if (!branchType) {
      return false;
    }

    if (runtimeType === "number") {
      return branchType === "number" || branchType === "integer";
    }

    return branchType === runtimeType;
  });

  return candidates.length === 1 ? candidates[0] : null;
}

function primitiveToCellValue(value: unknown): FlattenedRow["value"] {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  return JSON.stringify(value);
}

function createLeafRow(
  actualValue: unknown,
  path: string,
  apiSchema?: ExtendedDocument,
  schema?: SchemaNode | null
): FlattenedRow[] {
  const schemaPrimitiveType = apiSchema && schema ? getSchemaPrimitiveType(apiSchema, schema) : undefined;

  return [
    {
      path,
      value: primitiveToCellValue(actualValue),
      type: actualValue === undefined ? (schemaPrimitiveType ?? valueType(actualValue)) : valueType(actualValue),
    },
  ];
}

function flattenArrayItems<T>(
  items: T[],
  prefix: string,
  mapItem: (item: T, path: string) => FlattenedRow[]
): FlattenedRow[] {
  if (items.length === 0) {
    return [
      {
        path: prefix,
        value: "[]",
        type: "json",
      },
    ];
  }

  return items.flatMap((item, index) => {
    const path = prefix ? `${prefix}.${index}` : String(index);

    return mapItem(item, path);
  });
}

function resolveWalkerSchema(
  apiSchema: ExtendedDocument,
  schema?: SchemaNode | null
): ResolvedExtendedSchemaObject | null {
  const dereferenced = dereferenceSchema(apiSchema, schema);

  return resolveNullableSchema(dereferenced);
}

function getObjectSchemaPropertyKeys(schema: ResolvedExtendedSchemaObject): string[] {
  return schema.properties ? Object.keys(schema.properties) : [];
}

function getDiscriminatorPropertyName(schema: ResolvedExtendedSchemaObject): string | undefined {
  return schema.discriminator?.propertyName;
}

function getArrayItemSchema(schema: ResolvedExtendedSchemaObject): ResolvedExtendedSchemaObject | null {
  return schema.items ?? null;
}

function flattenObject(value: unknown, prefix = ""): FlattenedRow[] {
  if (value === null || value === undefined) {
    return [
      {
        path: prefix,
        value: "",
        type: value === null ? "null" : "undefined",
      },
    ];
  }

  if (typeof value !== "object") {
    return [
      {
        path: prefix,
        value: primitiveToCellValue(value),
        type: valueType(value),
      },
    ];
  }

  if (Array.isArray(value)) {
    return flattenArrayItems(value, prefix, (item, path) => flattenObject(item, path));
  }

  const entries = Object.entries(value as Record<string, unknown>);

  if (entries.length === 0) {
    return [
      {
        path: prefix,
        value: "{}",
        type: "json",
      },
    ];
  }

  return entries.flatMap(([key, childValue]) => {
    const path = prefix ? `${prefix}.${key}` : key;

    return flattenObject(childValue, path);
  });
}

export function walkSchemaForRows(
  actualValue: unknown,
  path: string,
  apiSchema?: ExtendedDocument,
  rawSchema?: SchemaNode | null
): FlattenedRow[] {
  if (!apiSchema || !rawSchema) {
    return flattenObject(actualValue, path);
  }

  const anyOfBranches = resolveAnyOfBranches(apiSchema, rawSchema);

  if (anyOfBranches) {
    const matchedBranch = pickMatchingAnyOfBranch(apiSchema, anyOfBranches, actualValue);

    if (!matchedBranch) {
      return flattenObject(actualValue, path);
    }

    return walkSchemaForRows(actualValue, path, apiSchema, matchedBranch);
  }

  const schema = resolveWalkerSchema(apiSchema, rawSchema);

  if (!schema) {
    return flattenObject(actualValue, path);
  }

  const discriminatorPropertyName = getDiscriminatorPropertyName(schema);
  const hasOneOf = Array.isArray(schema.oneOf) && schema.oneOf.length > 0;

  if (hasOneOf && discriminatorPropertyName) {
    const selectedVariant = resolveSchemaForValue(apiSchema, schema, actualValue);

    if (!selectedVariant) {
      return flattenObject(actualValue, path);
    }

    return walkSchemaForRows(actualValue, path, apiSchema, selectedVariant);
  }

  const propertyKeys = getObjectSchemaPropertyKeys(schema);
  const schemaPrimitiveType = getSchemaPrimitiveType(apiSchema, schema);

  if (propertyKeys.length > 0) {
    return propertyKeys.flatMap((key) => {
      const propertySchema = resolveObjectPropertySchema(apiSchema, key, schema);
      const actualChildValue = isRecord(actualValue) ? actualValue[key] : undefined;

      return walkSchemaForRows(actualChildValue, path ? `${path}.${key}` : key, apiSchema, propertySchema);
    });
  }

  if (schemaPrimitiveType === "array") {
    if (!Array.isArray(actualValue)) {
      return flattenObject(actualValue, path);
    }

    const itemSchema = getArrayItemSchema(schema);

    return flattenArrayItems(actualValue, path, (item, itemPath) =>
      walkSchemaForRows(item, itemPath, apiSchema, itemSchema)
    );
  }

  if (schemaPrimitiveType === "object") {
    return flattenObject(actualValue, path);
  }

  return createLeafRow(actualValue, path, apiSchema, schema);
}
